import { useEffect, useState, useSyncExternalStore } from "react";
import { CheckCircle2, AlertCircle, Loader2, RotateCw, X, ChevronDown, ChevronUp } from "lucide-react";
import { subscribeUploads, getUploads, retryUpload, clearFinished } from "@/lib/uploads";

const mb = (b) => (b < 1048576 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(b < 10485760 ? 1 : 0)} MB`);
const LABEL = { queued: "Wartet…", uploading: null, finishing: "Wird gespeichert…", done: "Fertig", error: "Fehlgeschlagen" };

function Row({ it }) {
  const color = it.status === "error" ? "bg-rose-500" : it.status === "done" ? "bg-emerald-500" : "bg-teal-400";
  return (
    <div className="px-4 py-3" data-testid="upload-item">
      <div className="flex items-center gap-2 mb-1.5">
        {it.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : it.status === "error" ? <AlertCircle className="h-4 w-4 text-rose-400 shrink-0" /> : <Loader2 className={`h-4 w-4 shrink-0 ${it.status === "queued" ? "text-slate-500" : "text-teal-300 animate-spin"}`} />}
        <span className="text-sm truncate flex-1" title={it.name}>{it.name}</span>
        <span className={`text-xs font-mono shrink-0 ${it.status === "error" ? "text-rose-300" : "text-slate-300"}`} data-testid="upload-item-percent">{LABEL[it.status] ?? `${it.progress}%`}</span>
        {it.status === "error" && <button onClick={() => retryUpload(it.id)} className="h-7 px-2 rounded-md bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1" data-testid="upload-retry-button"><RotateCw className="h-3 w-3" />Nochmal</button>}
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className={`h-full ${color} transition-[width] duration-300`} style={{ width: `${it.status === "error" ? 100 : it.progress}%` }} /></div>
      <p className={`text-[11px] mt-1 ${it.error ? "text-rose-300" : "text-slate-500"}`}>{it.error || mb(it.size)}</p>
    </div>
  );
}

export default function UploadPanel() {
  const items = useSyncExternalStore(subscribeUploads, getUploads);
  const [min, setMin] = useState(false);
  const busy = items.some((i) => i.status === "queued" || i.status === "uploading" || i.status === "finishing");
  useEffect(() => {
    if (!busy) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);
  if (!items.length) return null;
  const active = items.filter((i) => i.status === "queued" || i.status === "uploading" || i.status === "finishing").length;
  const failed = items.filter((i) => i.status === "error").length;
  const done = items.filter((i) => i.status === "done").length;
  return (
    <div className="fixed z-50 right-3 sm:right-6 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] lg:bottom-6 w-[calc(100%-1.5rem)] sm:w-96 rounded-2xl border border-border bg-[#33373f]/95 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden" data-testid="upload-panel">
      <div className="flex items-center gap-2 px-4 h-12 border-b border-border">
        <p className="text-sm font-semibold flex-1" data-testid="upload-panel-title">
          {active ? `${active} ${active === 1 ? "Datei lädt" : "Dateien laden"} hoch` : failed ? `${failed} fehlgeschlagen` : `${done} hochgeladen`}
        </p>
        <button onClick={() => setMin(!min)} className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/10" data-testid="upload-panel-toggle">{min ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
        {!active && <button onClick={clearFinished} className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/10" data-testid="upload-panel-close"><X className="h-4 w-4" /></button>}
      </div>
      {!min && <div className="max-h-[45vh] overflow-y-auto divide-y divide-border">{items.map((it) => <Row key={it.id} it={it} />)}</div>}
    </div>
  );
}
